import { ICreativeElement, OneOfElementDataNodes, ISerializedState, IState, PreloadAsset, IFontStyle, ISerializedCreativeData } from '../../domain';
import { parseColor, isColorProperty, isColor } from '../color.utils';
import { isContentSpan } from '../elements/rich-text/text-nodes';
import { isTextNode, isVideoNode, ICreativeInput } from '../nodes';

type ElementWithAsset = (ICreativeElement | OneOfElementDataNodes) & { assetId?: string, font?: IFontStyle };

export function getParsedCreativeData(serializedCreativeData: ISerializedCreativeData): ICreativeInput {
    return {
        ...serializedCreativeData,
        fill: parseColor(serializedCreativeData.fill),
        elements: serializedCreativeData.elements.map(e => deserializeElement(e))
    };
}

export function deserializeElement(serializedElement: ICreativeElement): OneOfElementDataNodes {
    const fill = serializedElement.fill
        ? parseColor(serializedElement.fill)
        : undefined;

    const element = {
        ...serializedElement,
        fill
    } as OneOfElementDataNodes;

    if (serializedElement.border) {
        const { color, thickness, style } = serializedElement.border;
        element.border = {
            thickness,
            style: style!,
            color: parseColor(color)
        };
    }

    if (serializedElement.shadows) {
        element.shadows = serializedElement.shadows.map(shadow => ({
            ...shadow,
            color: parseColor(shadow.color)
        }));
    }

    element.states = serializedElement.states.map(parseState);

    if (isTextNode(element) && isTextNode(serializedElement)) {
        if (serializedElement.content.spans.length > 1 && serializedElement.assetId !== undefined) {
            element.textColor = parseColor(serializedElement.textColor);
            element.content.spans = serializedElement.content.spans
                                                     .map(span => {
                                                         if (isContentSpan(span) && span.style.textColor) {
                                                             span.style.textColor = parseColor(span.style.textColor);
                                                         }
                                                         return span;
                                                     });

            const contentStyle = serializedElement.content.style;
            for (const key in contentStyle) {
                const styleValue = contentStyle[key];
                if (isColorProperty(key) && isColor(styleValue)) {
                    contentStyle[key] = parseColor(styleValue);
                }

                if (key === 'textShadow') {
                    if (isColorProperty(key) && isColor(styleValue)) {
                        contentStyle[key] = parseColor(styleValue);
                    }
                }
            }

            element.content.style = contentStyle;
        }
    }

    if (isVideoNode(element) && isVideoNode(serializedElement)) {
        const playbackButton = serializedElement.videoSettings.playbackButton;
        element.videoSettings = {
            ...serializedElement.videoSettings,
            playbackButton: {
                ...playbackButton,
                color: parseColor(playbackButton.color)
            }
        };
    }

    return element;
}

export function mapAssets(elements: ElementWithAsset[], preloadAssets: PreloadAsset[]): void {
    for (const element of elements) {
        if (isTextNode(element)) {
            if (element.content.spans.length > 1 && element.assetId !== undefined) {
                element.font = getFontStyleFromPreloadAssets_m(element.assetId, preloadAssets);
            }
        }
    }
}

function getFontStyleFromPreloadAssets_m(preloadAssetId: string, preloadAssets: PreloadAsset[]): IFontStyle {
    for (const preloadAsset of preloadAssets) {
        if (preloadAsset.id === preloadAssetId) {
            return preloadAsset.value;
        }
    }
    throw new Error('Could not find font style in preload assets.');
}

export function parseState(serializedState: ISerializedState): IState {
    function parse(obj: Partial<ISerializedState>): Partial<IState> {
        for (const key in obj) {
            const value = obj[key];

            let newValue;
            if (typeof value === 'object') {
                newValue = parse(value);
            } else if (typeof value === 'string' && isColorProperty(key) && isColor(value)) {
                newValue = parseColor(value);
            } else {
                newValue = value;
            }

            obj[key] = newValue;
        }
        return obj as Partial<IState>;
    }

    return parse(serializedState);
}
